import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getCodeData, getInitSheet, toNum } from "@shared/utils/CommonUtils";
import { buildJsAddinfoFromMeta } from "@shared/utils/AddInfoUtils";
import { global } from "@shared/libs/Global";
import SnSheet from "@shared/components/Grid/SnSheet";
import { globalInfo, menuInfo } from "@shared/type/CommonType";
import { showDialog } from "src/widgets/CommonDialog";
import SnResizableLNR from "@shared/components/SnResizableLNR";
import SnButton from "@shared/components/SnButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, Tooltip } from "@mui/material";

interface Props {
  Menuprops: menuInfo;
  searchParams: {
    cd_firm: string;
    cd_pjt: string;
    cd_unit: string;
  };
  controllerName: string;
  userInfo: globalInfo["userInfo"];
  modalRef?: any;
}

type EbomRow = Record<string, any>;
// sys_addinfo에서 가져오는 동적 부가항목 정의
type AddInfoMeta = {
  cd_flag: string;
  nm_flag?: string;
  tp_data?: string;
  dc_qt_valid?: string;
  yn_null?: string;
  cd_clas?: string;
};

const SN_PMS_PJT_REG_004_POP_001 = forwardRef(
  ({ Menuprops, searchParams, controllerName, userInfo }: Props, ref) => {
    const [columns, setColums] = useState<any[]>([]);
    const [dataSource1, setDataSource1] = useState<any>();
    const gridref1 = useRef<any>(null);
    // 저장 시 row -> js_addinfo(jsonb)로 묶기 위해 메뉴별 addinfo 메타를 보관
    const addInfoMetaRef = useRef<AddInfoMeta[]>([]);

    const existingEbomRef = useRef<any[]>([]);
    const masItemCacheRef = useRef<Set<string>>(new Set());
    const comboCacheRef = useRef<
      Map<string, { set: Set<string>; codeMap: Map<string, string> }>
    >(new Map());

    let width1 = "99%";
    const menuSize = localStorage.getItem("menuSize");
    if (menuSize) {
      const menu = JSON.parse(menuSize);
      if (menu.ExcelUpload) {
        width1 = menu.ExcelUpload.grid1Width
          ? menu.ExcelUpload.grid1Width
          : width1;
      }
    }
    const [grid1Width, setGrid1Width] = useState<string | number>(width1);

    const norm = (v: any) =>
      String(v ?? "")
        .trim()
        .toUpperCase();

    const makeCodeMap = (list: Array<{ code: string; name: string }>) => {
      const m = new Map<string, string>();
      (list ?? []).forEach((x) => {
        if (!x) return;
        const code = String(x.code ?? "");
        const name = String(x.name ?? "");
        if (code) m.set(norm(code), code);
        if (name) m.set(norm(name), code);
      });
      return m;
    };

    const toCode = (
      input: any,
      codeMap: Map<string, string>,
      fallback?: string
    ) => {
      const key = norm(input);
      if (!key) return fallback ?? "";
      return codeMap.get(key) ?? fallback ?? String(input ?? "");
    };

    const toNumber = (val: any, fallback = 0) => {
      if (val === null || val === undefined) return fallback;
      if (typeof val === "string" && val.trim() === "") return fallback;
      return toNum(val);
    };

    useEffect(() => {
      const fetchInit = async () => {
        try {
          const rtn: any = await getInitSheet({});
          // 1) 메뉴별 addinfo 정의(sys_addinfo) 조회
          const addInfoData: any = await fnGetDataSet({
            id: "sys_addinfo_pop_01_01",
            params: {
              cd_firm: userInfo.cd_firm,
              cd_menu: Menuprops?.cd_menu ?? "",
            },
          });
          const addInfoRows: AddInfoMeta[] = addInfoData?.[0] ?? [];
          addInfoMetaRef.current = addInfoRows;

          // 2) sheet1 컬럼은 getInitSheet 공통 결과를 그대로 사용
          const baseSheet = Array.isArray(rtn?.grid?.sheet1)
            ? rtn.grid.sheet1
            : [];
          const mergedSheet = [...baseSheet];

          mergedSheet.push({
            cd_column: "error_msg",
            nm_column: "검증이력",
            qt_width: 240,
            tp_column: "text",
            fg_visible: "Y",
            yn_required: "N",
            dc_editcon: "1 == 1",
            editable: false,
          });

          setColums(mergedSheet);
          gridref1.current.resetGridData();
        } catch (error) {}
      };
      fetchInit();
    }, [Menuprops?.cd_menu, userInfo.cd_firm]);

    useEffect(() => {
      const loadExisting = async () => {
        if (
          !searchParams?.cd_firm ||
          !searchParams?.cd_pjt ||
          !searchParams?.cd_unit
        )
          return;

        try {
          const params = {
            id: "sn_pms_pjt_reg_004_01",
            params: {
              cd_firm: searchParams.cd_firm,
              cd_pjt: searchParams.cd_pjt,
              cd_unit: searchParams.cd_unit,
            },
          };
          const data: any = await fnGetDataSet(params);
          existingEbomRef.current = data?.[0] ?? [];
        } catch (error) {
          existingEbomRef.current = [];
        }
      };
      loadExisting();
    }, [searchParams]);

    const onDelete = () => {
      const focusedCell = gridref1.current.api.getFocusedCell();
      const startRowIndex = focusedCell?.rowIndex ?? 0;
      gridref1.current?.deleteRowByIndex(startRowIndex);
    };

    // 검증 실패 메시지를 행의 error_msg 컬럼에 누적 기록
    const setErrorMessage = (node: any, msg: string) => {
      const prev = node.data?.error_msg
        ? String(node.data.error_msg) + " | "
        : "";
      gridref1.current.api
        .getRowNode(node.id)
        ?.setDataValue("error_msg", prev + msg);
    };

    // 빈 행(검증/저장 대상 제외) 여부 판별
    const isEmptyRow = (row: EbomRow): boolean => {
      const checkFields = ["no_level", "cd_matl", "cd_item"];
      return checkFields.every((field) => !row[field]);
    };

    // 엑셀 업로드 데이터의 BOM 구조/코드/형식을 검증
    const Validation = async (): Promise<boolean> => {
      const api = gridref1.current?.api;
      if (!api) return false;

      const formCdFirm = searchParams?.cd_firm ?? userInfo.cd_firm;
      const formCdPjt = searchParams?.cd_pjt;
      const formCdUnit = searchParams?.cd_unit; // unit 파라미터명에 모품목 값 전달

      if (!formCdPjt || !formCdUnit) {
        await showDialog("alter", "프로젝트/UNIT이 없습니다.");
        return false;
      }

      const nodes: any[] = [];
      api.forEachNode((n: any) => {
        if (n?.data && !isEmptyRow(n.data)) {
          api.getRowNode(n.id)?.setDataValue("error_msg", "");
          nodes.push(n);
        }
      });

      if (nodes.length === 0) {
        await showDialog("alter", "검증할 데이터가 없습니다.");
        return false;
      }

      // mas_item 코드 캐시: 최초 1회 로딩 후 재사용 (cd_item, cd_matl 존재 여부 검증에 사용)
      const masItems: Set<string> =
        masItemCacheRef.current.size > 0
          ? masItemCacheRef.current
          : new Set<string>(
              (await getCodeData("mas_item", "")).map((x: any) =>
                String(x.cd_item ?? "")
              )
            );
      masItemCacheRef.current = masItems;

      // SnComboBox 컬럼 코드 캐시 (code/name 모두 set에 저장)
      const comboCols = (columns || []).filter((c: any) => {
        const tp = String(c?.tp_column || "")
          .trim()
          .toLowerCase();
        return tp === "sncombobox" && c?.cd_column && c?.cd_clas;
      });
      const comboCache = comboCacheRef.current;
      for (const col of comboCols) {
        const clas = String(col.cd_clas || "");
        if (!clas) continue;
        if (!comboCache.has(clas)) {
          const codes = await getCodeData(clas, "");
          const set = new Set<string>();
          const codeMap = makeCodeMap(codes || []);
          codes.forEach((x: any) => {
            if (x?.code) set.add(norm(x.code));
            if (x?.name) set.add(norm(x.name));
          });
          comboCache.set(clas, { set, codeMap });
        }
      }

      const existingData = existingEbomRef.current || [];

      const dateRegex = /^\d{8}$/;
      const isBlank = (v: any) =>
        v === undefined || v === null || String(v).trim() === "";

      const maxNoHstByKey = new Map<string, number>();
      existingData.forEach((r: any) => {
        const key = [r.cd_firm, r.cd_pjt, r.cd_unit, r.cd_item, r.cd_matl].join(
          "|"
        );
        const cur = maxNoHstByKey.get(key) ?? 0;
        const nh = toNumber(r.no_hst, 1);
        maxNoHstByKey.set(key, Math.max(cur, nh));
      });

      let hasError = false;
      const childrenMap = new Map<string, string[]>();
      const nodeById: Record<string, any> = {};
      const parentEdge: Record<string, string> = {};
      // BOM 레벨 검증용 스택
      const levelStack: Record<number, any> = {};
      // 첫 행의 레벨을 루트 기준 레벨로 사용
      const rootLevel = toNumber(nodes[0]?.data?.no_level ?? 0);

      for (let idx = 0; idx < nodes.length; idx++) {
        const node = nodes[idx];
        const row = node.data;
        const curLevel = toNumber(row.no_level, 0);
        const id = String(node.id);
        nodeById[id] = node;

        const derived: EbomRow = {
          ...row,
          cd_firm: row.cd_firm || formCdFirm,
          cd_pjt: row.cd_pjt || formCdPjt,
          cd_unit: formCdUnit, // cd_unit은 전역(최상위 품목)
          cd_item: row.cd_item || undefined,
          no_hst: toNumber(row.no_hst, 1),
        };

        // no_level 기본 형식 체크
        if (idx != 0) {
          // 첫번째 모품목 제외
          if (
            isBlank(row.no_level) ||
            isNaN(Number(row.no_level)) ||
            curLevel <= 0
          ) {
            setErrorMessage(node, "no_level은 1 이상의 숫자여야 합니다.");
            hasError = true;
          }
        }

        // 첫 행만 루트로 고정 (레벨과 무관하게 cd_item = cd_unit)
        if (idx === 0) {
          // 루트 행은 기준 레벨만 허용
          if (curLevel !== rootLevel) {
            setErrorMessage(
              node,
              `루트 행 레벨이 올바르지 않습니다.(${curLevel})`
            );
            hasError = true;
          }
          derived.cd_item = formCdUnit;
          levelStack[curLevel] = node;
        } else {
          // 레벨 건너뜀 체크 (예: 3 다음 5)
          const prevLevel = toNumber(nodes[idx - 1]?.data?.no_level, rootLevel);
          if (curLevel > prevLevel + 1) {
            setErrorMessage(
              node,
              `레벨이 연속되지 않습니다. 이전 레벨(${prevLevel}) 다음은 최대 ${prevLevel + 1}까지 가능합니다.`
            );
            hasError = true;
          }

          // 부모 존재 체크 (현재 레벨 - 1)
          const parentNode = levelStack[curLevel - 1] || null;
          if (!parentNode) {
            setErrorMessage(
              node,
              `레벨 ${curLevel} 부모(레벨 ${curLevel - 1})를 찾을 수 없습니다.`
            );
            hasError = true;
          } else {
            const parentId = String(parentNode.id);
            if (!childrenMap.has(parentId)) childrenMap.set(parentId, []);
            childrenMap.get(parentId)!.push(id);
            parentEdge[id] = parentId;
            const parentMatl = parentNode.data?.cd_matl ?? "";
            derived.cd_item = parentMatl;

            const parentCdDummy03 = (parentNode.data?.cd_dummy03 || "").trim();
            if (
              childrenMap.get(parentId)?.length &&
              parentMatl &&
              parentCdDummy03 &&
              !["5", "반제품"].includes(parentCdDummy03)
            ) {
              setErrorMessage(
                parentNode,
                `부모(${parentMatl})는 하위 자식 허용 코드가 아닙니다.`
              );
              hasError = true;
            }
          }
          levelStack[curLevel] = node;
          Object.keys(levelStack)
            .map((k) => Number(k))
            .filter((lvl) => lvl > curLevel)
            .forEach((lvl) => delete levelStack[lvl]);
        }

        const cdItem = derived.cd_item;
        if (!cdItem || !masItems.has(String(cdItem))) {
          setErrorMessage(node, `모품목(${cdItem ?? ""}) 코드가 없습니다.`);
          hasError = true;
        }

        if (!derived.cd_matl || !masItems.has(String(derived.cd_matl))) {
          setErrorMessage(
            node,
            `자품목(${derived.cd_matl ?? ""}) 코드가 없습니다.`
          );
          hasError = true;
        }

        const numericFields = [
          "no_level",
          "no_hst",
          "no_line",
          "qt",
          "um",
          "qt_rcv",
          "qt_prd",
          "qt_isu",
        ];
        numericFields.forEach((key) => {
          const val = derived[key];
          if (!isBlank(val) && isNaN(Number(val))) {
            setErrorMessage(node, `${key} 값이 숫자가 아닙니다.`);
            hasError = true;
          }
        });

        Object.keys(row || {})
          .filter((k) => k.startsWith("dt"))
          .forEach((k) => {
            if (!isBlank(derived[k]) && !dateRegex.test(String(derived[k]))) {
              setErrorMessage(node, `${k} 형식이 올바르지 않습니다(YYYYMMDD).`);
              hasError = true;
            }
          });

        // SnComboBox 값 유효성 체크 (code/name 모두 허용)
        for (const col of comboCols) {
          const val = derived[col.cd_column];
          const clas = String(col.cd_clas || "");
          if (isBlank(val) || !clas || !comboCache.has(clas)) continue;
          const entry = comboCache.get(clas)!;
          if (!entry.set.has(norm(val))) {
            setErrorMessage(
              node,
              `${col.nm_column || col.cd_column}(${val})가 코드(${clas})에 없습니다.`
            );
            hasError = true;
          }
        }
      }

      childrenMap.forEach((kids, pid) => {
        const seen = new Set<string>();
        kids.forEach((cid) => {
          const child = nodeById[cid];
          const key = `${pid}|${child.data.cd_matl ?? ""}`;
          if (seen.has(key)) {
            setErrorMessage(child, "동일 부모-자식이 중복되었습니다.");
            hasError = true;
          }
          seen.add(key);
        });
      });

      const cycleCheck = () => {
        const graph = new Map<string, string[]>();
        Object.keys(parentEdge).forEach((cid) => {
          const pid = parentEdge[cid];
          const parentMatl = nodeById[pid]?.data?.cd_matl ?? "";
          const childMatl = nodeById[cid]?.data?.cd_matl ?? "";
          if (!parentMatl || !childMatl) return;
          if (!graph.has(parentMatl)) graph.set(parentMatl, []);
          graph.get(parentMatl)!.push(childMatl);
        });

        const visiting = new Set<string>();
        const visited = new Set<string>();
        const dfs = (node: string): boolean => {
          if (visiting.has(node)) return true;
          if (visited.has(node)) return false;
          visiting.add(node);
          const next = graph.get(node) || [];
          for (const n of next) {
            if (dfs(n)) return true;
          }
          visiting.delete(node);
          visited.add(node);
          return false;
        };

        for (const key of graph.keys()) {
          if (dfs(key)) return true;
        }
        return false;
      };

      if (cycleCheck()) {
        hasError = true;
        await showDialog("error", "BOM 구조에 순환 참조가 존재합니다.");
      }

      nodes.forEach((node) => {
        const row = node.data;
        const key = [
          row.cd_firm || formCdFirm,
          row.cd_pjt || formCdPjt,
          formCdUnit,
          row.cd_item,
          row.cd_matl,
        ].join("|");
        const maxHst = maxNoHstByKey.get(key) ?? 0;
        const curHst = toNumber(row.no_hst, 1);
        if (curHst < maxHst) {
          setErrorMessage(
            node,
            `no_hst(${curHst})가 기존 최대(${maxHst})보다 작습니다.`
          );
          hasError = true;
        }
      });

      if (hasError) {
        await showDialog(
          "error",
          "검증에 실패했습니다. 오류 메시지를 확인해 주세요."
        );
        return false;
      }
      return true;
    };

    // 시트 데이터를 BE 저장용 EBOMH/EBOML payload 구조로 변환
    const buildSavePayload = async () => {
      const api = gridref1.current?.api;
      if (!api) return null;

      const formCdPjt = searchParams?.cd_pjt ?? "";
      const formCdUnit = searchParams?.cd_unit ?? "";

      const rows: EbomRow[] = [];
      api.forEachNode((n: any) => {
        if (n?.data && !isEmptyRow(n.data)) rows.push(n.data);
      });

      // SnComboBox 코드맵 (Validation 캐시 사용, 없으면 로드)
      const comboCache = comboCacheRef.current;
      const comboCols = (columns || []).filter((c: any) => {
        const tp = String(c?.tp_column || "")
          .trim()
          .toLowerCase();
        return tp === "sncombobox" && c?.cd_column && c?.cd_clas;
      });
      for (const col of comboCols) {
        const clas = String(col.cd_clas || "");
        if (!clas) continue;
        if (!comboCache.has(clas)) {
          const codes = await getCodeData(clas, "");
          const set = new Set<string>();
          const codeMap = makeCodeMap(codes || []);
          codes.forEach((x: any) => {
            if (x?.code) set.add(norm(x.code));
            if (x?.name) set.add(norm(x.name));
          });
          comboCache.set(clas, { set, codeMap });
        }
      }

      const existingMap = new Map<string, EbomRow>();
      (existingEbomRef.current || []).forEach((r: any) => {
        const key = [
          r.cd_firm,
          r.cd_pjt,
          r.cd_unit,
          r.cd_item,
          toNumber(r.no_hst, 0),
          r.cd_matl,
        ].join("|");
        existingMap.set(key, r);
      });

      const pkForCompare = [
        "cd_firm",
        "cd_pjt",
        "cd_unit",
        "cd_item",
        "no_hst",
        "cd_matl",
      ];
      const insert: EbomRow[] = [];
      const update: EbomRow[] = [];

      // 1차 패스: 레벨/부모 매핑, 그룹별 no_line max 집계
      // 주의: 객체 참조 비교 대신, 레벨별 "행 인덱스"를 같이 저장해서 부모 탐색을 안정화한다.
      const levelStack: Record<number, EbomRow> = {};
      const levelIndexStack: Record<number, number> = {};
      const parentItemMap = new Map<number, string>(); // idx -> parent cd_item
      const parentIndexMap = new Map<number, number | null>(); // idx -> parent row idx
      const derivedRows: { idx: number; derived: EbomRow }[] = [];
      const rootLevel = toNumber(rows[0]?.no_level ?? 0);

      const maxNoLineByGroup = new Map<string, number>();
      existingMap.forEach((v) => {
        const g = [v.cd_firm, v.cd_pjt, v.cd_unit, v.cd_item].join("|");
        const cur = maxNoLineByGroup.get(g) ?? 0;
        const nl = toNumber(v.no_line, 0);
        if (nl > cur) maxNoLineByGroup.set(g, nl);
      });

      rows.forEach((row, idx) => {
        const derived: EbomRow = {
          ...row,
          cd_firm: row.cd_firm || userInfo.cd_firm,
          cd_pjt: row.cd_pjt || formCdPjt,
          cd_unit: formCdUnit,
          cd_item: row.cd_item || undefined,
          no_hst: toNumber(row.no_hst, 1),
          // no_line/no_seq는 자동 채번(사용자 입력 무시)
          no_line: 0,
          no_seq: 0,
        };

        const curLevel = toNumber(row.no_level, 0);
        if (idx === 0) {
          derived.cd_item = formCdUnit;
          levelStack[curLevel] = { ...derived };
          levelIndexStack[curLevel] = idx;
          parentItemMap.set(idx, "");
          parentIndexMap.set(idx, null);
        } else {
          const parent = levelStack[curLevel - 1];
          if (parent) {
            derived.cd_item = parent.cd_matl || derived.cd_item;
            parentItemMap.set(idx, parent.cd_matl || "");
            parentIndexMap.set(idx, levelIndexStack[curLevel - 1] ?? null);
          } else {
            parentItemMap.set(idx, "");
            parentIndexMap.set(idx, null);
          }
          levelStack[curLevel] = { ...derived };
          levelIndexStack[curLevel] = idx;
          Object.keys(levelStack)
            .map((k) => Number(k))
            .filter((lvl) => lvl > curLevel)
            .forEach((lvl) => delete levelStack[lvl]);
          Object.keys(levelIndexStack)
            .map((k) => Number(k))
            .filter((lvl) => lvl > curLevel)
            .forEach((lvl) => delete levelIndexStack[lvl]);
        }

        const groupKey = [
          derived.cd_firm,
          derived.cd_pjt,
          derived.cd_unit,
          derived.cd_item,
        ].join("|");
        const curMax = maxNoLineByGroup.get(groupKey) ?? 0;
        if (derived.no_line > curMax) {
          maxNoLineByGroup.set(groupKey, derived.no_line);
        }

        derivedRows.push({ idx, derived });
      });

      // 2차 패스: no_line 채번, 콤보 코드 치환, insert/update 결정
      derivedRows.forEach(({ idx, derived }) => {
        if (idx === 0) {
          // 최초 등록 시 루트(0레벨)도 저장할 수 있도록 고정값 세팅
          derived.no_line = 0;
          derived.no_line_p = 0;
          derived.no_seq = 0;
          derived.no_level = toNumber(derived.no_level, 0);
          derived.cd_item = formCdUnit;
        } else {
          // no_line 자동 채번
          const groupKey = [
            derived.cd_firm,
            derived.cd_pjt,
            derived.cd_unit,
            derived.cd_item,
          ].join("|");
          const curMax = maxNoLineByGroup.get(groupKey) ?? 0;
          const next = curMax + 1;
          derived.no_line = next;
          maxNoLineByGroup.set(groupKey, next);

          // no_line_p 자동 주입 (부모 no_line 기준, 없으면 0)
          const parentIdx = parentIndexMap.get(idx);
          if (parentIdx !== null && parentIdx !== undefined && parentIdx >= 0) {
            const parentRow = derivedRows[parentIdx]?.derived;
            derived.no_line_p = toNumber(parentRow?.no_line, 0);
          } else {
            derived.no_line_p = 0;
          }

          // no_seq 자동 채번 (no_line과 동일하게 설정)
          derived.no_seq = derived.no_line;
        }

        // SnComboBox 코드 치환
        for (const col of comboCols) {
          const clas = String(col.cd_clas || "");
          if (!clas || !comboCache.has(clas)) continue;
          const codeMap = comboCache.get(clas)!.codeMap;
          const val = derived[col.cd_column];
          const converted = codeMap.get(norm(val));
          if (converted) {
            derived[col.cd_column] = converted;
          }
        }

        const key = [
          derived.cd_firm,
          derived.cd_pjt,
          derived.cd_unit,
          derived.cd_item,
          derived.no_hst,
          derived.cd_matl,
        ].join("|");

        const existing = existingMap.get(key);
        if (!existing) {
          insert.push({ ...derived, fg_iud: "I" });
          return;
        }

        const compareColumns = columns
          .map((c: any) => c.cd_column)
          .filter((c: string) => !!c && !pkForCompare.includes(c));

        const hasDiff = compareColumns.some((col) => {
          const prevVal = existing[col];
          const newVal = derived[col];
          return String(prevVal ?? "") !== String(newVal ?? "");
        });

        if (hasDiff) {
          update.push({
            ...existing,
            ...derived,
            no_line: existing.no_line ?? derived.no_line,
            fg_iud: "U",
          });
        }
      });

      // EBOML 저장용 라인 매핑 (H 키 포함)
      const toLine = (row: EbomRow) => {
        const cdFirm = row.cd_firm || userInfo.cd_firm;
        const cdPjt = row.cd_pjt || formCdPjt;
        const cdUnit = row.cd_unit || formCdUnit;
        const cdItem = row.cd_item || formCdUnit;
        const noHst = toNumber(row.no_hst, 0);
        const noRev = row.no_rev ?? "0";
        const stBom = row.st_bom ?? "1";
        // 4) 그리드의 동적 부가항목 컬럼값을 js_addinfo(jsonb)로 묶는다.
        const js_addinfo = buildJsAddinfoFromMeta(
          row,
          addInfoMetaRef.current || []
        );

        return {
          // UUID 컬럼에는 빈 문자열("")을 보내지 않고 null 처리
          no_id_ebomh: row.no_id_ebomh ? String(row.no_id_ebomh) : null,
          no_line: toNumber(row.no_line, 0),
          no_line_p: toNumber(row.no_line_p, 0),
          no_level: toNumber(row.no_level, 0),
          no_seq: toNumber(row.no_seq, 0),
          cd_matl: row.cd_matl ?? "",
          qt_just: row.qt_just ?? row.qt ?? 0,
          rt_loss: row.rt_loss ?? 0,
          qt_need: row.qt_need ?? row.qt ?? 0,
          dc_rmk: row.dc_rmk ?? row.dc_emk ?? "",
          fg_eco: row.fg_eco ?? null,
          cd_cb: row.cd_cb ?? "",
          no_cbs: row.no_cbs ?? "",
          cd_firm: cdFirm,
          cd_pjt: cdPjt,
          cd_unit: cdUnit,
          cd_item: cdItem,
          no_hst: noHst,
          no_rev: noRev,
          st_bom: stBom,
          dc_revision: row.dc_revision ?? "",
          cd_emp: row.cd_emp ?? userInfo.cd_emp ?? "",
          cd_gp: row.cd_gp ?? userInfo.cd_gp ?? "",
          cd_dept: row.cd_dept ?? userInfo.cd_dept ?? "",
          cd_user_reg: row.cd_user_reg ?? userInfo.cd_user ?? "",
          yn_use: row.yn_use ?? "Y",
          js_addinfo,
        };
      };

      // BE는 라인 payload를 기준으로 헤더를 생성/매핑
      return {
        pms_pjt_eboml: {
          insert: insert.map(toLine),
          update: update.map(toLine),
          modifiedRowcount: insert.length + update.length,
        },
      };
    };

    const onSave = async () => {
      try {
        const api = gridref1.current?.api;
        if (!api) return false;

        const isValid = await Validation();
        if (!isValid) return false;

        const payload = await buildSavePayload();
        if (!payload) return false;

        if (payload.pms_pjt_eboml.modifiedRowcount === 0) {
          await showDialog("alter", "변경된 데이터가 없습니다.");
          return false;
        }
        const saveResult = await fnSave(controllerName, payload);
        if (!saveResult) {
          return false;
        }
        gridref1.current.resetUpdate();
        gridref1.current.resetGridData();
        return true;
      } catch (error: any) {
        await showDialog(
          "error",
          error?.message || "저장 중 오류가 발생했습니다."
        );
      }
      return false;
    };

    const onResizeStopH = (width: number) => {
      setGrid1Width(width);
    };

    useImperativeHandle(ref, () => ({
      onDelete,
      onSave,
      getSelectedValue: async () => {
        const ok = await onSave();
        return { excelUpload: !!ok };
      },
    }));

    const onGrid1BtnsClick = async (id: string) => {
      if (id == "aButton_Init") {
        gridref1.current.resetGridData();
      }

      if (id == "aButton_Validation") {
        const isValid = await Validation();
        if (isValid) {
          await showDialog("alter", "모든 데이터 검증이 완료되었습니다.");
        }
      }
    };

    return (
      <div className="h-full flex flex-col">
        <div className="w-full flex flex-row space-x-2 flex-grow overflow-x-auto">
          <SnResizableLNR
            width={grid1Width}
            minWidth={130}
            onResizeStop={onResizeStopH}
            enable={{ right: true }}
            menuName={Menuprops.cd_menu ?? ""}
            defaultSize={{ grid2Width: grid1Width }}
          >
            <SnSheet
              ref={gridref1}
              enableAddRow={true}
              columnConfig={columns}
              rowData={dataSource1}
              isEditable={true}
              title={Menuprops.nm_menu}
              header={
                <div>
                  <SnButton
                    text={"초기화"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Init");
                    }}
                  />
                  <SnButton
                    text={"검증체크"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Validation");
                    }}
                  />

                  <Tooltip title="Delete">
                    <IconButton
                      onClick={onDelete}
                      sx={{
                        color: "gray",
                        "&:hover": {
                          color: "blue",
                          transform: "scale(1.2)",
                          transition: "all 0.3s ease",
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              }
              codeDataList={[]}
              compareFields={[]}
            />
          </SnResizableLNR>
        </div>
      </div>
    );
  }
);

export default SN_PMS_PJT_REG_004_POP_001;
